/**
 * authenticatedFetch — the UAT-portal tester auth transport.
 *
 * Story: epic-uat-portal-001-8.
 *
 * A thin wrapper over the platform `fetch` for the UAT portal's authenticated
 * tester requests (bug submit, status board — epic-uat-portal-001-10/-11). Every
 * tester-gated oracle-bridge call goes through THIS, so the bearer injection and
 * the 401 lifecycle live in ONE place.
 *
 * Mirrors dao-suite's `member-authenticated-fetch.ts` SHAPE — bearer-header merge
 * that never clobbers a caller-set Authorization, `credentials: 'include'` — but
 * DROPS the identity-service silent-refresh machinery entirely. The UAT portal
 * uses a standalone lightweight account with NO refresh token: on a 401 the
 * session is simply over. So on 401 this wrapper CLEARS the persisted session
 * (token + account — see testerTokenStore -15) and redirects the tester to
 * `/login`, where a fresh magic link re-authenticates them. There is no second
 * refresh, no token-family rotation — none of that exists for this account type.
 * This 401 clear is the authoritative revocation/expiry gate for the persisted
 * session.
 */

import { getTesterToken, clearTesterToken } from './testerTokenStore';

/**
 * Merge an `Authorization: Bearer` header into a RequestInit's headers without
 * dropping caller-supplied headers, accepting the three Headers shapes the Fetch
 * API allows (`Headers`, `string[][]`, `Record<string,string>`).
 */
function withBearer(options: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(options?.headers);
  // Do not clobber an explicit Authorization header a caller already set.
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...options, headers };
}

/**
 * Where to send a tester whose session has expired. Replaceable so a test can
 * assert the redirect without a real navigation, and so App can later wire a
 * router-aware navigate if a hard reload is ever undesirable. Default performs a
 * hard navigation to `/login` (the magic-link re-auth front door).
 */
let redirectToLogin: () => void = () => {
  window.location.assign('/login');
};

/**
 * Override the unauthenticated-redirect behaviour (testing / custom wiring).
 * Pass `undefined` to restore the default hard navigation to `/login`.
 * @internal
 */
export function _setRedirectToLogin(fn: (() => void) | undefined): void {
  redirectToLogin = fn ?? (() => window.location.assign('/login'));
}

/**
 * Session-aware fetch for UAT-portal tester backend calls.
 *
 * - Injects `Authorization: Bearer <sessionToken>` when a token is stored
 *   (unless the caller already set an Authorization header).
 * - Sends `credentials: 'include'` to mirror the dao-suite transport.
 * - On a 401 it CLEARS the in-memory token and redirects to `/login` (no silent
 *   refresh — the magic link is the re-auth), then returns the 401 response so a
 *   caller that wants to render an error can still inspect it.
 *
 * @param url - the request URL
 * @param options - standard RequestInit (Authorization added unless already present)
 * @returns the fetch Response
 */
export async function authenticatedFetch(
  url: string,
  options?: RequestInit,
): Promise<Response> {
  const token = getTesterToken();
  const init: RequestInit = {
    ...(token ? withBearer(options, token) : options ?? {}),
    credentials: 'include',
  };

  const response = await fetch(url, init);

  if (response.status === 401) {
    // Session is over. There is no refresh token for a UAT-portal account, so
    // clear the dead token and bounce to the magic-link login. The response is
    // still returned in case the caller wants to surface it.
    clearTesterToken();
    redirectToLogin();
  }

  return response;
}
